import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { downloadDraftFile } from "../../utils/utils.ts";
import "./DropZone.css";
import { FaDownload, FaTrash } from "react-icons/fa";
import {
  FieldValues,
  useController,
  UseControllerProps
} from "react-hook-form";

function DropZone<T extends FieldValues>(props: UseControllerProps<T>) {
  const { name, control, rules, defaultValue } = props;

  const {
    field,
    fieldState: { error }
  } = useController({
    name,
    control,
    rules,
    defaultValue
  });

  const [files, setFiles] = useState<any[]>(field.value ?? []);
  function removeIndividualFile(fileName: string) {
    const updatedFiles = files.filter(file => file.name !== fileName);
    setFiles(updatedFiles);
    // Update the field value in react-hook-form
    field.onChange(updatedFiles);
  }

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: acceptedFiles => {
      const allFiles = [...files, ...acceptedFiles].map(file =>
        Object.assign(file, {
          preview: URL.createObjectURL(file)
        })
      );
      setFiles(allFiles);
      field.onChange(allFiles);
    }
  });

  // If the form sets this field's value to an empty array (or clears it),
  // we sync that change here as well:
  useEffect(() => {
    if ((!field.value || field.value.length === 0) && files.length > 0) {
      setFiles([]);
    }
  }, [field.value]);

  // Cleanup: revoke object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      files.forEach(file => URL.revokeObjectURL(file.preview));
    };
  }, [files]);

  const thumbs = files.map(file => {
    // Example condition for bigger sets of files:
    if (files.length > 3) {
      return (
        <div className="drop-zone-list" key={file.name}>
          <div
            className="drop-zone-list-download-container"
            onClick={() => downloadDraftFile(file)}
          >
            <FaDownload />
            <p className="drop-zone-listitem">
              {file.name + " " + Math.round(file.size / 1000) + " kB"}
            </p>
          </div>
          <div className="drop-zone-remove-icon">
            <FaTrash onClick={() => removeIndividualFile(file.name)} />
          </div>
        </div>
      );
    } else {
      if (file.type === "image/jpeg" || file.type === "image/png") {
        return (
          <div className={"thumb img"} key={file.name}>
            <div className={"thumbInner"}>
              <img
                src={file.preview}
                className={"img"}
                alt={"img"}
                onClick={() => downloadDraftFile(file)}
              />
              <div className="drop-zone-remove-icon">
                <FaTrash onClick={() => removeIndividualFile(file.name)} />
              </div>
            </div>
          </div>
        );
      } else {
        return (
          <div className="thumb-wrapper" key={file.name}>
            <div className={"thumb"}>
              <div
                className={"thumbInner non-image"}
                onClick={() => downloadDraftFile(file)}
              >
                <strong>{Math.round(file.size / 1000) + " kB"}</strong>
              </div>
            </div>
            <div className="drop-zone-remove-icon">
              <FaTrash onClick={() => removeIndividualFile(file.name)} />
            </div>
          </div>
        );
      }
    }
  });

  return (
    <div className="file-section">
      <section className="dropzone-container">
        <div {...getRootProps({ className: "dropzone-component" })}>
          <input {...getInputProps()} />
          <p className="dropzone-text">
            Klicka eller dra och släpp för att ladda upp relevanta filer
          </p>
        </div>
      </section>

      <div
        className={`dropzone-remove-container ${
          files.length > 3 ? "list" : ""
        }`}
      >
        <div className={`thumbsContainer ${files.length > 3 ? "list" : ""}`}>
          {thumbs}
        </div>
      </div>
      <div
        className={`dropzone-remove ${files.length === 0 ? "hide" : ""}`}
        onClick={() => {
          setFiles([]);
          field.onChange([]);
        }}
      >
        Radera alla filer
      </div>
      {error && <p className="field-error-msg">{error.message}</p>}
    </div>
  );
}

export default DropZone;
